import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { AsyncPipe, DecimalPipe, NgClass, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { FlexModule } from '@angular/flex-layout/flex';
import { FormsModule } from '@angular/forms';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { CuttingPanelHelper } from 'cloud-shared-lib';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { DirectivesModule } from '../../../directives/directives.module';
import { DisableInputSpinEventsDirective } from '../../../directives/disable-input-spin-events.directive';
import { CustomFormsModule } from '../../custom-forms/custom-forms.module';

@Component({
  selector: 'csl-inside-cut-with-angle-input',
  templateUrl: './inside-cut-with-angle-input.component.html',
  standalone: true,
  imports: [
    FlexModule,
    DirectivesModule,
    MatLegacyFormFieldModule,
    NgClass,
    ExtendedModule,
    DisableInputSpinEventsDirective,
    FormsModule,
    MatLegacyInputModule,
    CustomFormsModule,
    AsyncPipe,
    MatLegacyCheckboxModule,
    NgIf,
    MatLegacySelectModule,
    DecimalPipe,
  ],
})
export class InsideCutWithAngleInputComponent {
  _X = 0;
  _Y = 0;
  _X1 = 0;
  _Y1 = 0;
  _XA = 90;
  _YA = 90;
  _R = 0;

  mode: 'angle' | 'side' = 'angle';

  paramsCalcError = '';

  variableParamsChanged$: Observable<unknown>;

  @Input() editable = true;

  @Input() xMax = 0;
  @Input() xMin = 0;
  @Input() yMax = 0;
  @Input() yMin = 0;
  @Input() rMax = 0;
  @Input() rMin = 0;

  @Output() xChange: EventEmitter<number> = new EventEmitter<number>();
  @Input() set x(value: number) {
    this._X = value;
  }

  @Output() yChange: EventEmitter<number> = new EventEmitter<number>();
  @Input() set y(value: number) {
    this._Y = value;
  }

  @Output() xAChange: EventEmitter<number> = new EventEmitter<number>();
  @Input() set xA(value: number) {
    this._XA = value;
  }

  @Output() yAChange: EventEmitter<number> = new EventEmitter<number>();
  @Input() set yA(value: number) {
    this._YA = value;
  }

  @Output() x1Change: EventEmitter<number> = new EventEmitter<number>();
  @Input() set x1(value: number) {
    this._X1 = value;
  }

  @Output() y1Change: EventEmitter<number> = new EventEmitter<number>();
  @Input() set y1(value: number) {
    this._Y1 = value;
  }

  @Output() rChange: EventEmitter<number> = new EventEmitter<number>();
  @Input() set r(value: number) {
    this._R = value;
  }

  @Input()
  get dense(): boolean {
    return this._dense;
  }
  set dense(value: BooleanInput) {
    this._dense = coerceBooleanProperty(value);
  }
  _dense = false;

  constructor() {}

  isSideMode(): boolean {
    return this.mode === 'side';
  }

  recalculateVariableParams(): void {
    this.paramsCalcError = '';
    const calculatedNumberPrecision = 3;

    try {
      if (this.isSideMode()) {
        const angles = CuttingPanelHelper.insideCut.findAngles(this._X, this._Y, this._X1, this._Y1);
        this._XA = _.round(angles.XA, calculatedNumberPrecision);
        this._YA = _.round(angles.YA, calculatedNumberPrecision);
        this.xAChange.emit(this._XA);
        this.yAChange.emit(this._YA);
      } else {
        const sides = CuttingPanelHelper.insideCut.findSubSides(this._X, this._Y, this._XA, this._YA);
        this._X1 = _.round(sides.x1, calculatedNumberPrecision);
        this._Y1 = _.round(sides.y1, calculatedNumberPrecision);
        this.x1Change.emit(this._X1);
        this.y1Change.emit(this._Y1);
      }
    } catch (e) {
      if (e instanceof Error) {
        this.paramsCalcError = e.message;
      }
    }
  }

  paramChanged(
    eventEmitter: EventEmitter<number>,
    param: 'XA' | 'YA' | 'X1' | 'Y1' | 'R' | 'X' | 'Y',
    value: number
  ): void {
    this[`_${param}`] = value;
    eventEmitter.next(value);
    this.recalculateVariableParams();
  }
}
